import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Brand } from "./Brand";
import api from "../api";
import { getPrerender } from "../prerender";
import { SiteFooter } from "./site/SiteFooter";
import { useSiteNav } from "./site/useSiteNav";
import { ArrowLeft, Building2, Mail } from "lucide-react";

export const useLegalContent = () => {
  const [content, setContent] = useState(() => getPrerender()?.legal || null);
  useEffect(() => {
    api.get("/legal-content").then((r) => setContent(r.data.content)).catch((e) => { console.error("Caricamento contenuti legali non riuscito:", e); setContent({}); });
  }, []);
  return content;
};

// Renderer leggero: "# Titolo" -> heading, "- voce" -> elenco, righe vuote separano i paragrafi.
export const LegalBody = ({ text }) => {
  if (!text) return null;
  const lines = String(text).split("\n");
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push({ type: "ul", items: list }); list = null; } };
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    if (line.startsWith("# ")) { flush(); blocks.push({ type: "h", text: line.slice(2) }); }
    else if (line.startsWith("- ")) { if (!list) list = []; list.push(line.slice(2)); }
    else if (line.trim() === "") { flush(); }
    else { flush(); blocks.push({ type: "p", text: line }); }
  });
  flush();
  return (
    <div className="space-y-4" data-testid="legal-body">
      {blocks.map((b, i) => {
        if (b.type === "h") return <h2 key={i} className="text-xl font-black text-white mt-8 first:mt-0">{b.text}</h2>;
        if (b.type === "ul") return <ul key={i} className="list-disc pl-5 space-y-1.5 text-[14.5px] text-gray-300 leading-relaxed">{b.items.map((it, j) => <li key={j}>{it}</li>)}</ul>;
        return <p key={i} className="text-[14.5px] text-gray-300 leading-relaxed">{b.text}</p>;
      })}
    </div>
  );
};

export const LegalLayout = ({ title, updatedLabel, content, children }) => {
  const nav = useSiteNav();
  return (
    <div className="min-h-screen bg-[#0d1b2f] text-white">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0d1b2f]/85 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="shrink-0" data-testid="legal-brand"><Brand dark className="text-[22px]" /></Link>
          <Link to="/" data-testid="legal-back"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-[#ff7e00] hover:text-[#ff7e00] px-4 py-2 text-[13px] font-bold transition-colors">
            <ArrowLeft size={15} /> Torna al sito
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight" data-testid="legal-title">{title}</h1>

        {/* Titolare del trattamento */}
        {content && (
          <div className="mt-8 rounded-2xl bg-white/[0.04] border border-white/10 p-5" data-testid="legal-controller">
            <div className="text-[12px] font-black uppercase tracking-widest text-[#ffb066] mb-3">Titolare del trattamento</div>
            <div className="grid sm:grid-cols-2 gap-y-2.5 gap-x-6 text-[14px]">
              <div className="flex items-center gap-2.5"><Building2 size={16} className="text-[#ff7e00] shrink-0" /><span className="font-bold">{content.controller_name}</span></div>
              <div className="flex items-center gap-2.5"><Mail size={16} className="text-[#ff7e00] shrink-0" /><a href={`mailto:${content.privacy_email}`} className="hover:text-[#ff7e00]">{content.privacy_email}</a></div>
              <div className="text-gray-300">Sede: {content.controller_address}</div>
              <div className="text-gray-300">P.IVA / C.F.: {content.controller_vat}</div>
            </div>
          </div>
        )}

        <div className="mt-8">{children}</div>
      </main>

      <SiteFooter pages={nav.pages} />
    </div>
  );
};
